import { ChangeEventHandler, useState } from 'react'

import { useTranslation } from 'react-i18next'
import { connect, DataItem, useDataItem } from 'scrivito'

import { AddressTabList } from './AddressTabList'
import { useAddressTabData } from './useAddressTabData'
import { TabEmptyState } from '../TabEmptyState/TabEmptyState'
import { Button } from '@/Atomic/Molecules/Button'
import { Search } from '@/Atomic/Molecules/Inputs/Search'
import { ScrollablePanel } from '@/Atomic/Templates/ScrollablePanel'
import { Sublist, SublistFooter } from '@/Atomic/Templates/Sublist'
import { DataClassAddressDrawer } from '@/Atomic/Widgets/DataClassDrawer/DataClass.AddressDrawer'
import { DataItemAddressDrawer } from '@/Atomic/Widgets/DataItemDrawer'
import { DataClassName } from '@/Data/const'
import { useFlashSublistItem } from '@/hooks/useFlashSublistItem'
import { useModalHandler } from '@/hooks/useModalHandler'
import { useDataItemDrawer } from '@/utils/CRUD/hooks/useDataItemDrawer'
import { getDataItemTitle, getSchemaName } from '@/utils/dataClassMap'
import { getEntityLabelKey } from '@/utils/getEntityLabelKey'
import { getSchema } from '@/utils/getSchema'

import './AddressTab.scss'

const INITIAL_LIMIT = 5

interface AddressTabProps {
  searchInputName: string
}

export const AddressTab: React.FC<AddressTabProps> = connect(
  ({ searchInputName }) => {
    const { t } = useTranslation()
    const contactItem = useDataItem()
    const subjectId = contactItem?.id() || ''
    const contactName = getDataItemTitle(contactItem)

    const [search, setSearch] = useState('')
    const [limit, setLimit] = useState(INITIAL_LIMIT)

    const { flashId: flashAddressId, flash: flashAddress } =
      useFlashSublistItem()
    const {
      isOpen: isCreateOpen,
      openModal: openCreateDrawer,
      closeModal: closeCreateDrawer,
    } = useModalHandler()
    const {
      drawerItem,
      setDrawerItem,
      isDrawerOpen,
      openDrawer,
      closeDrawer,
      openDrawerId,
    } = useDataItemDrawer()

    const { schema: addressSchema } = getSchema(
      getSchemaName(DataClassName.ADDRESS),
    )

    const onSearchChange: ChangeEventHandler<HTMLInputElement> = (event) => {
      setSearch(event.target.value)
    }

    const {
      listAddresses,
      localSearch,
      findRelationForAddress,
      nextId,
      previousId,
      count,
      loadMore,
    } = useAddressTabData({
      subjectId,
      limit,
      search,
      currentDrawerId: drawerItem?.id(),
    })

    const header = (
      <div className="tab-header-wrapper">
        <div className="search-filter">
          <Search
            name={searchInputName}
            searchWrapperClassName="tab-header-search"
            onChange={onSearchChange}
            value={search}
            placeholder={t('search.title')}
          />
        </div>
        {!!subjectId && (
          <div className="tab-header-actions">
            <Button
              title={t('common.create')}
              size="sm"
              iconSize="sm"
              iconColor="white"
              onClick={openCreateDrawer}
              icon="add"
            />
          </div>
        )}
      </div>
    )

    const isItemsEmpty = listAddresses.length === 0

    return (
      <>
        <Sublist className="address-tab">
          <ScrollablePanel header={header}>
            {isItemsEmpty ? (
              <div className="sublist-wrapper">
                {search ? (
                  <TabEmptyState
                    title={t('tabs.noResults.title')}
                    description={t('tabs.noResults.searchCreate', {
                      entity: t(getEntityLabelKey(DataClassName.ADDRESS), {
                        count: 1,
                      }),
                    })}
                    cta={
                      subjectId
                        ? {
                            label: t('common.create'),
                            icon: 'add',
                            onClick: openCreateDrawer,
                          }
                        : undefined
                    }
                  />
                ) : (
                  <TabEmptyState
                    title={t('tabs.empty.addresses.title')}
                    description={t('tabs.empty.addresses.description')}
                    cta={
                      subjectId
                        ? {
                            label: t('common.create'),
                            icon: 'add',
                            onClick: openCreateDrawer,
                          }
                        : undefined
                    }
                  />
                )}
              </div>
            ) : (
              <div className="sublist-wrapper">
                <AddressTabList
                  listAddresses={listAddresses}
                  addressSchema={addressSchema}
                  localSearch={localSearch}
                  findRelationForAddress={findRelationForAddress}
                  flashAddressId={flashAddressId}
                  onOpenAddress={(address: DataItem) => {
                    setDrawerItem(address)
                    openDrawer()
                  }}
                />

                {!search && (
                  <SublistFooter
                    amount={listAddresses.length}
                    total={count}
                    labelKey={getEntityLabelKey(DataClassName.ADDRESS)}
                    onLoadMore={
                      loadMore ? () => setLimit((prev) => prev + 5) : undefined
                    }
                  />
                )}
              </div>
            )}
          </ScrollablePanel>
        </Sublist>

        {drawerItem && (
          <DataItemAddressDrawer
            key={drawerItem.id()}
            isOpen={isDrawerOpen}
            close={closeDrawer}
            dataItem={drawerItem}
            openDrawerId={openDrawerId}
            nextId={nextId}
            previousId={previousId}
          />
        )}

        <DataClassAddressDrawer
          contactId={subjectId}
          contactName={contactName}
          isOpen={isCreateOpen}
          close={closeCreateDrawer}
          onCreated={flashAddress}
        />
      </>
    )
  },
)
